import { useUserStore } from '@/store'

// 用户角色常量
export const ROLES = {
  STUDENT: 'student',
  TEACHER: 'teacher',
  ADMIN: 'admin'
}

// 权限常量
export const PERMISSIONS = {
  // 用户管理权限
  VIEW_USERS: 'view_users',
  CREATE_USERS: 'create_users',
  UPDATE_USERS: 'update_users',
  DELETE_USERS: 'delete_users',
  // 课程管理权限
  VIEW_COURSES: 'view_courses',
  CREATE_COURSES: 'create_courses',
  UPDATE_COURSES: 'update_courses',
  DELETE_COURSES: 'delete_courses',
  MANAGE_COURSE_CONTENT: 'manage_course_content',
  MANAGE_COURSE_CATEGORIES: 'manage_course_categories',
  VIEW_COURSE_STATISTICS: 'view_course_statistics',
  // 考试管理权限
  VIEW_EXAMS: 'view_exams',
  TAKE_EXAMS: 'take_exams',
  VIEW_OWN_RESULTS: 'view_own_results',
  VIEW_LEADERBOARD: 'view_leaderboard',
  CREATE_EXAMS: 'create_exams',
  UPDATE_EXAMS: 'update_exams',
  DELETE_EXAMS: 'delete_exams',
  MANAGE_QUESTIONS: 'manage_questions',
  IMPORT_QUESTIONS: 'import_questions',
  GRADE_EXAMS: 'grade_exams',
  VIEW_EXAM_STATISTICS: 'view_exam_statistics',
  // 资源管理权限
  MANAGE_RESOURCES: 'manage_resources',
  // AI辅助功能
  USE_CHATBOT: 'use_chatbot',
  AI_ASSISTED_QUESTION_CREATION: 'ai_assisted_question_creation',
  // 个人资料管理
  MANAGE_OWN_PROFILE: 'manage_own_profile',
  // 证书管理
  MANAGE_CERTIFICATE_TEMPLATES: 'manage_certificate_templates',
  ISSUE_CERTIFICATES: 'issue_certificates',
  VIEW_CERTIFICATES: 'view_certificates',
  VIEW_OWN_CERTIFICATES: 'view_own_certificates',
  // 平台管理
  MANAGE_POLICIES: 'manage_policies',
  CONFIGURE_HOME_PAGE: 'configure_home_page',
  MANAGE_PLATFORM_SETTINGS: 'manage_platform_settings',
  // 监控管理
  MONITOR_COURSE_LEARNING: 'monitor_course_learning',
  MONITOR_EXAMS: 'monitor_exams',
  // 数据看板
  VIEW_DASHBOARD: 'view_dashboard',
  EXPORT_DATA: 'export_data',
  // 会议管理权限
  VIEW_MEETINGS: 'view_meetings',
  CREATE_MEETINGS: 'create_meetings',
  UPDATE_MEETINGS: 'update_meetings',
  DELETE_MEETINGS: 'delete_meetings'
}

// 权限组常量
export const PERMISSION_GROUPS = {
  // 学生基础权限组
  STUDENT_BASE_PERMISSIONS: [
    // 基本学习权限
    PERMISSIONS.VIEW_COURSES,
    PERMISSIONS.VIEW_EXAMS,
    PERMISSIONS.TAKE_EXAMS,
    PERMISSIONS.VIEW_OWN_RESULTS,
    PERMISSIONS.VIEW_LEADERBOARD,
    PERMISSIONS.USE_CHATBOT,
    PERMISSIONS.MANAGE_OWN_PROFILE,
    PERMISSIONS.VIEW_OWN_CERTIFICATES,
    // 会议权限
    PERMISSIONS.VIEW_MEETINGS
  ],
  
  // 教师基础权限组
  TEACHER_BASE_PERMISSIONS: [
    // 学生权限
    PERMISSIONS.VIEW_COURSES,
    PERMISSIONS.VIEW_EXAMS,
    PERMISSIONS.TAKE_EXAMS,
    PERMISSIONS.VIEW_OWN_RESULTS,
    PERMISSIONS.VIEW_LEADERBOARD,
    PERMISSIONS.USE_CHATBOT,
    PERMISSIONS.MANAGE_OWN_PROFILE,
    PERMISSIONS.VIEW_OWN_CERTIFICATES,
    // 课程管理权限
    PERMISSIONS.CREATE_COURSES,
    PERMISSIONS.UPDATE_COURSES,
    PERMISSIONS.DELETE_COURSES,
    PERMISSIONS.MANAGE_COURSE_CONTENT,
    PERMISSIONS.MANAGE_COURSE_CATEGORIES,
    PERMISSIONS.VIEW_COURSE_STATISTICS,
    // 考试管理权限
    PERMISSIONS.CREATE_EXAMS,
    PERMISSIONS.UPDATE_EXAMS,
    PERMISSIONS.DELETE_EXAMS,
    PERMISSIONS.MANAGE_QUESTIONS,
    PERMISSIONS.IMPORT_QUESTIONS,
    PERMISSIONS.GRADE_EXAMS,
    // 其他教师权限
    PERMISSIONS.MANAGE_RESOURCES,
    PERMISSIONS.AI_ASSISTED_QUESTION_CREATION,
    PERMISSIONS.VIEW_EXAM_STATISTICS,
    // 会议管理权限
    PERMISSIONS.VIEW_MEETINGS,
    PERMISSIONS.CREATE_MEETINGS,
    PERMISSIONS.UPDATE_MEETINGS,
    PERMISSIONS.DELETE_MEETINGS
  ],
  
  // 管理员基础权限组
  ADMIN_BASE_PERMISSIONS: [
    ...Object.values(PERMISSIONS)
  ],
  
  // 证书管理权限组
  CERTIFICATE_MANAGEMENT: [
    PERMISSIONS.MANAGE_CERTIFICATE_TEMPLATES,
    PERMISSIONS.ISSUE_CERTIFICATES,
    PERMISSIONS.VIEW_CERTIFICATES
  ],
  
  // 平台管理权限组
  PLATFORM_MANAGEMENT: [
    PERMISSIONS.MANAGE_POLICIES,
    PERMISSIONS.CONFIGURE_HOME_PAGE,
    PERMISSIONS.MANAGE_PLATFORM_SETTINGS
  ],
  
  // 监控管理权限组
  MONITORING_MANAGEMENT: [
    PERMISSIONS.MONITOR_COURSE_LEARNING,
    PERMISSIONS.MONITOR_EXAMS
  ],
  
  // 数据看板权限组
  DASHBOARD_PERMISSIONS: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.EXPORT_DATA
  ]
}

// 角色权限映射
export const ROLE_PERMISSIONS = {
  'student': PERMISSION_GROUPS.STUDENT_BASE_PERMISSIONS,
  'teacher': PERMISSION_GROUPS.TEACHER_BASE_PERMISSIONS,
  'admin': PERMISSION_GROUPS.ADMIN_BASE_PERMISSIONS
}

// 权限指令：根据用户权限显示或隐藏元素
export const setupPermissionDirective = (app) => {
  // 检查单个权限
  app.directive('permission', {
    mounted(el, binding) {
      const userStore = useUserStore()
      const requiredPermission = binding.value
      
      // 确保有用户信息
      if (!userStore.isAuthenticated || !userStore.currentUser) {
        el.style.display = 'none'
        return
      }
      
      // 检查用户是否有权限
      const hasPermission = userStore.hasPermission(requiredPermission)
      
      if (!hasPermission) {
        el.style.display = 'none'
      }
    },
    updated(el, binding) {
      // 组件更新时重新检查权限
      const userStore = useUserStore()
      const requiredPermission = binding.value
      
      // 确保有用户信息
      if (!userStore.isAuthenticated || !userStore.currentUser) {
        el.style.display = 'none'
        return
      }
      
      // 检查用户是否有权限
      const hasPermission = userStore.hasPermission(requiredPermission)
      
      if (!hasPermission) {
        el.style.display = 'none'
      } else {
        // 如果之前被隐藏但现在有权限了，恢复显示
        el.style.display = ''
      }
    }
  })
  
  // 检查用户角色
  app.directive('role', {
    mounted(el, binding) {
      const userStore = useUserStore()
      const requiredRole = binding.value
      
      // 确保有用户信息
      if (!userStore.isAuthenticated || !userStore.currentUser) {
        el.style.display = 'none'
        return
      }
      
      // 检查用户角色
      const hasRole = userStore.hasRole(requiredRole)
      
      if (!hasRole) {
        el.style.display = 'none'
      }
    },
    updated(el, binding) {
      // 组件更新时重新检查角色
      const userStore = useUserStore()
      const requiredRole = binding.value
      
      // 确保有用户信息
      if (!userStore.isAuthenticated || !userStore.currentUser) {
        el.style.display = 'none'
        return
      }
      
      // 检查用户角色
      const hasRole = userStore.hasRole(requiredRole)
      
      if (!hasRole) {
        el.style.display = 'none'
      } else {
        // 如果之前被隐藏但现在有角色了，恢复显示
        el.style.display = ''
      }
    }
  })
  
  // 检查多个权限（任一权限满足即可）
  app.directive('any-permission', {
    mounted(el, binding) {
      const userStore = useUserStore()
      const requiredPermissions = Array.isArray(binding.value) ? binding.value : [binding.value]
      
      // 确保有用户信息
      if (!userStore.isAuthenticated || !userStore.currentUser) {
        el.style.display = 'none'
        return
      }
      
      // 检查是否有任一权限
      const hasAnyPermission = requiredPermissions.some(permission => 
        userStore.hasPermission(permission)
      )
      
      if (!hasAnyPermission) {
        el.style.display = 'none'
      }
    }
  })
  
  // 检查多个角色（任一角色满足即可）
  app.directive('any-role', {
    mounted(el, binding) {
      const userStore = useUserStore()
      const requiredRoles = Array.isArray(binding.value) ? binding.value : [binding.value]
      
      // 确保有用户信息
      if (!userStore.isAuthenticated || !userStore.currentUser) {
        el.style.display = 'none'
        return
      }
      
      // 检查是否有任一角色
      const hasAnyRole = requiredRoles.some(role => 
        userStore.hasRole(role)
      )
      
      if (!hasAnyRole) {
        el.style.display = 'none'
      }
    }
  })
}

// 权限检查辅助函数
export const checkPermission = (permission) => {
  const userStore = useUserStore()
  return userStore.hasPermission(permission)
}

// 直接导出hasPermission函数（供App.vue使用）
export const hasPermission = checkPermission

export const checkRole = (role) => {
  const userStore = useUserStore()
  return userStore.hasRole(role)
}

export const checkAnyPermission = (...permissions) => {
  const userStore = useUserStore()
  return permissions.some(permission => userStore.hasPermission(permission))
}

export const checkAnyRole = (...roles) => {
  const userStore = useUserStore()
  return roles.some(role => userStore.hasRole(role))
}

// 权限过滤器：在模板中可以使用的权限过滤函数
export const permissionFilters = {
  hasPermission: checkPermission,
  hasRole: checkRole,
  hasAnyPermission: checkAnyPermission,
  hasAnyRole: checkAnyRole
}

// 所有常量已在文件开头定义